import React, { useState } from 'react';
import { CityStats } from '../../types';
import { playUiSfx } from '../../utils/uiAudio';
import { ModalShell, PanelHeader } from './kit';
import {
  SOC_ACTION_COSTS,
  SOC_THREAT_REDUCTION,
} from '../../src/application/use-cases/ExecuteSocActionUseCase';
import type { SocActionType } from '../../src/application/dto/ExecuteSocActionDto';

interface SOCPanelProps {
    stats: CityStats;
    onSOCAction: (action: SocActionType) => void;
    onClose: () => void;
    hasJumpGate: boolean;
}

type SocActionDef = {
    id: SocActionType;
    label: string;
    description: string;
    borderClass: string;
    hoverClass: string;
    labelClass: string;
    detailClass: string;
    urgent?: boolean;
};

const SOC_ACTIONS: SocActionDef[] = [
    {
        id: 'restore',
        label: 'FLUSH LOGS',
        description: 'Minor threat reduction',
        borderClass: 'border-green-800/50',
        hoverClass: 'hover:bg-green-900/20',
        labelClass: 'text-green-400',
        detailClass: 'text-green-700',
    },
    {
        id: 'contain',
        label: 'CONTAIN BREACH',
        description: 'Significant threat reduction',
        borderClass: 'border-yellow-800/50',
        hoverClass: 'hover:bg-yellow-900/20',
        labelClass: 'text-yellow-500',
        detailClass: 'text-yellow-700',
    },
    {
        id: 'ransom',
        label: 'PAY RANSOM',
        description: 'Emergency decryption — high cost',
        borderClass: 'border-red-800/50 bg-red-950/10',
        hoverClass: 'hover:bg-red-900/20',
        labelClass: 'text-red-500 animate-pulse',
        detailClass: 'text-red-700',
        urgent: true,
    },
];

const SOCPanel: React.FC<SOCPanelProps> = ({ stats, onSOCAction, onClose, hasJumpGate }) => {
    const [pendingAction, setPendingAction] = useState<SocActionType | null>(null);

    const history = stats.threatHistory || Array(20).fill(0);
    const maxVal = 100;
    const width = 100;
    const height = 50;
    const step = width / (history.length - 1);
    const points = history.map((val, i) => `${i * step},${height - ((val / maxVal) * height)}`).join(' ');
    const areaPath = `${points} ${width},${height} 0,${height}`;

    const pendingDef = pendingAction
        ? SOC_ACTIONS.find((a) => a.id === pendingAction)
        : null;
    const pendingCost = pendingAction ? SOC_ACTION_COSTS[pendingAction] : 0;
    const pendingThreatDrop = pendingAction ? SOC_THREAT_REDUCTION[pendingAction] : 0;
    const canAfford = pendingAction ? stats.budget >= pendingCost : false;
    const threatAfter = pendingAction
        ? Math.max(0, stats.threat - pendingThreatDrop)
        : stats.threat;
    const budgetAfter = pendingAction ? stats.budget - pendingCost : stats.budget;

    const handleConfirm = () => {
        if (!pendingAction || !canAfford) {
            playUiSfx('error');
            return;
        }
        playUiSfx('success');
        onSOCAction(pendingAction);
        setPendingAction(null);
    };

    return (
        <ModalShell
            variant="center"
            zLevel="modal"
            overlayClassName="bg-black/90 backdrop-blur-md"
            className="max-w-5xl"
        >
            <div className="w-full bg-black border border-green-900 rounded-lg shadow-[0_0_50px_rgba(34,197,94,0.1)] font-mono relative grid grid-cols-1 md:grid-cols-4 gap-6 overflow-hidden">
                <PanelHeader
                    theme="soc"
                    title="SOC DASHBOARD"
                    subtitle="Security Operations Center"
                    icon="🛡️"
                    onClose={() => { playUiSfx('click'); onClose(); }}
                    closeLabel="Close Interface"
                    className="col-span-1 md:col-span-4 px-6 pt-6 pb-4"
                />

                <div className="col-span-1 md:col-span-3 bg-black border border-green-900/30 p-4 relative mx-6 mb-0">
                    <h3 className="text-xs font-bold text-green-600 mb-2 uppercase tracking-widest">Live Threat Vector</h3>
                    <div className="h-48 w-full bg-green-900/5 border border-green-900/30 overflow-hidden relative">
                        <svg viewBox="0 0 100 50" className="w-full h-full" preserveAspectRatio="none">
                            <path d={areaPath} fill="rgba(34, 197, 94, 0.1)" />
                            <polyline points={points} fill="none" stroke={stats.threat > 70 ? '#ef4444' : '#22c55e'} strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                        </svg>
                        <div className="absolute top-2 right-2 text-3xl font-black text-green-500 tabular-nums">{stats.threat.toFixed(1)}%</div>
                    </div>
                </div>

                <div className="col-span-1 border-l border-green-900/30 pl-6 pr-6 pb-6 flex flex-col justify-center gap-4">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Response Protocols</h3>
                    {!hasJumpGate ? (
                        <div className="p-4 bg-red-950/30 border border-red-900 text-red-500 text-xs font-bold text-center animate-pulse">
                            CONNECTION LOST<br />ADMIN GATE OFFLINE
                        </div>
                    ) : pendingDef ? (
                        <div
                            data-testid="soc-confirm-panel"
                            className="p-3 border border-cyan-800/50 bg-cyan-950/20 space-y-3"
                        >
                            <div className="text-[10px] uppercase tracking-widest text-cyan-500 font-bold">
                                Confirm action
                            </div>
                            <div className={`text-xs font-bold ${pendingDef.labelClass}`}>{pendingDef.label}</div>
                            <div className="space-y-1 text-[10px] text-slate-400">
                                <div className="flex justify-between">
                                    <span>Cost</span>
                                    <span className={canAfford ? 'text-red-400 font-bold' : 'text-red-500 font-bold'}>
                                        -${pendingCost.toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Threat delta</span>
                                    <span className="text-emerald-400 font-bold">-{pendingThreatDrop}%</span>
                                </div>
                                <div className="flex justify-between border-t border-slate-800 pt-1">
                                    <span>Threat after</span>
                                    <span className="text-green-400 font-bold">{threatAfter.toFixed(1)}%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Budget after</span>
                                    <span className={canAfford ? 'text-green-400 font-bold' : 'text-red-400 font-bold'}>
                                        ${budgetAfter.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                            {!canAfford && (
                                <div className="text-[10px] text-red-400 font-bold">Insufficient funds</div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    data-testid="soc-confirm-cancel"
                                    onClick={() => { playUiSfx('click'); setPendingAction(null); }}
                                    className="flex-1 py-2 text-[10px] font-bold uppercase border border-slate-700 text-slate-400 hover:bg-slate-900"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    data-testid="soc-confirm-execute"
                                    disabled={!canAfford}
                                    onClick={handleConfirm}
                                    className={`flex-1 py-2 text-[10px] font-bold uppercase border transition-all ${
                                        canAfford
                                            ? 'border-emerald-600 text-emerald-400 hover:bg-emerald-900/30'
                                            : 'border-slate-700 text-slate-600 cursor-not-allowed'
                                    }`}
                                >
                                    Execute
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {SOC_ACTIONS.map((action) => {
                                const cost = SOC_ACTION_COSTS[action.id];
                                const threatDrop = SOC_THREAT_REDUCTION[action.id];
                                const affordable = stats.budget >= cost;

                                return (
                                    <button
                                        key={action.id}
                                        type="button"
                                        data-testid={`soc-action-${action.id}`}
                                        disabled={!affordable}
                                        onClick={() => {
                                            playUiSfx(affordable ? 'click' : 'error');
                                            setPendingAction(action.id);
                                        }}
                                        className={`w-full text-left p-3 border group transition-all ${action.borderClass} ${action.hoverClass} ${
                                            !affordable ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    >
                                        <div className={`text-xs font-bold ${action.labelClass}`}>{action.label}</div>
                                        <div className={`text-[10px] ${action.detailClass}`}>
                                            ${cost.toLocaleString()} • -{threatDrop}% Threat
                                        </div>
                                        {!affordable && (
                                            <div className="text-[9px] text-red-500 mt-0.5">Need ${cost.toLocaleString()}</div>
                                        )}
                                    </button>
                                );
                            })}
                        </>
                    )}
                </div>
            </div>
        </ModalShell>
    );
};

export default SOCPanel;